/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Video from 'components/Video/Video';
import Thumbs from './Thumbs/Thumbs';
import './VideoGallery.scss';

/**
 * This is the VideoGallery component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function VideoGallery({ videos, ...props }) {
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  if (!videos[active]) return null;

  const thumbs = videos.map(({ thumb, video }, index) => ({
    index,
    ...thumb,
    title: video.text,
    duration: video.duration,
  }));

  const played = thumbs.slice(0, active + 1);
  const queue = thumbs.slice(active + 1);

  function renderThumbs() {
    if (videos.length < 2) {
      return null;
    }

    return (
      <Thumbs
        list={[...queue, ...played]}
        active={active}
        onSelect={index => {
          setActive(index);
          setAutoplay(true);
        }}
        {...props}
      />
    );
  }

  return (
    <div className="block video-gallery" appear="true">
      <Video
        key={active}
        autoPlay={autoplay}
        onEnded={() => {
          setActive(index => {
            if (index + 1 < videos.length) {
              return index + 1;
            }

            return 0;
          });
          setAutoplay(true);
        }}
        {...videos[active].video}
      />
      {renderThumbs()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
VideoGallery.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      video: PropTypes.object.isRequired,
      thumb: PropTypes.object.isRequired,
    })
  ).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
VideoGallery.defaultProps = {
  videos: [],
};

export default VideoGallery;
